import { IconChevronRight, IconCircleCheck, IconClock } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { badgeVariants } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface SourceFreshness {
  state: "current" | "pending";
  updatedAt: string | null;
  throughDate: string | null;
}

interface DashboardDataFreshnessProps {
  ads?: SourceFreshness;
  sales?: SourceFreshness;
  unavailable: boolean;
  loading: boolean;
  canOpenSettings: boolean;
}

export function DashboardDataFreshness({
  ads,
  sales,
  unavailable,
  loading,
  canOpenSettings,
}: DashboardDataFreshnessProps) {
  const { t, i18n } = useTranslation("dashboard");
  const dates = [ads?.throughDate, sales?.throughDate];
  const times = [ads?.updatedAt, sales?.updatedAt];
  const throughDate = dates.every((date): date is string => Boolean(date)) ? dates.sort()[0] : null;
  const datesMatch = Boolean(ads?.throughDate && ads.throughDate === sales?.throughDate);
  const updatedAt = times.every((time): time is string => Boolean(time)) ? times.sort()[0] : null;
  const current =
    !unavailable &&
    datesMatch &&
    Boolean(updatedAt) &&
    Boolean(throughDate) &&
    ads?.state === "current" &&
    sales?.state === "current";
  const formattedTime = updatedAt
    ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(
        new Date(updatedAt),
      )
    : null;
  const formattedDate = throughDate
    ? new Intl.DateTimeFormat(i18n.language, {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      }).format(new Date(`${throughDate}T00:00:00Z`))
    : null;
  const label = unavailable
    ? t("freshness.unavailable")
    : loading
      ? t("freshness.loading")
      : current && formattedTime && formattedDate
        ? t("freshness.current", { date: formattedDate, time: formattedTime })
        : formattedTime
          ? t("freshness.pendingUpdated", { time: formattedTime })
          : t("freshness.pending");
  const content = (
    <>
      {current ? <IconCircleCheck aria-hidden /> : <IconClock aria-hidden />}
      <span>{label}</span>
      {canOpenSettings ? <IconChevronRight aria-hidden /> : null}
    </>
  );
  const className = cn(
    badgeVariants({ variant: current ? "success" : "neutral", size: "default" }),
    "h-auto min-h-6 max-w-full justify-start gap-1.5 whitespace-normal py-1 text-left font-normal [&>svg]:shrink-0",
    canOpenSettings && "cursor-pointer hover:opacity-80",
  );
  return canOpenSettings ? (
    <Link to="/settings" search={{ tab: "amazon", dataDetails: true }} className={className}>
      {content}
    </Link>
  ) : (
    <span className={className} role="status">
      {content}
    </span>
  );
}
